import {useState} from 'react'
import {useNavigate} from 'react-router'
import {faDownload} from '@fortawesome/free-solid-svg-icons'
import {fromMaybe} from '@freckle/maybe'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {PATHS} from '@freckle/educator-entities/ts/common/helpers/paths'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import {
  downloadFile,
  extractDataAndHeaders,
  fancyClient,
} from '@freckle/educator-entities/ts/openapi-clients'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Alert} from '@freckle/educator-materials/ts/components/alert'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import ApiHelper from '@freckle/school/ts/common/helpers/api-helper'
import {
  logErrorAsUnhandled,
  logError,
} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {TeacherTabFilterQueryParamsT} from '@freckle/school/ts/common/routers/routes'
import {Routes, TEACHER_FILTER_QUERY_PARAM_TAG} from '@freckle/school/ts/common/routers/routes'
import {
  fetchTeacherTableRows,
  removeAffiliation,
} from '@freckle/school/ts/manage-teachers/api-helpers/api-teacher-table'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import {InviteTeacherForm} from '@freckle/school/ts/manage-teachers/components/invite-teacher-form'
import type {TeacherTabFilteringT} from '@freckle/school/ts/manage-teachers/components/teacher-tab/'
import {ImpersonateModal} from '@freckle/school/ts/manage-teachers/components/teacher-tab/impersonate-modal'
import {RemoveTeacherModal} from '@freckle/school/ts/manage-teachers/components/teacher-tab/remove-teacher-modal'
import TeacherTable from '@freckle/school/ts/manage-teachers/components/teacher-tab/teacher-table'
import type {TableRowT} from '@freckle/school/ts/manage-teachers/components/teacher-tab/teacher-table'
import {getInitialFilters} from '@freckle/school/ts/manage-teachers/components/teacher-tab/teacher-table/utils'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import TeacherTabFilter from './teacher-tab-filter'
import {teachersMissingEmailAlert, downloadLink} from './teacher-tab.module.scss'

const IMPERSONATION_ERROR_NOTIF_WAIT = 10

type ModalToShowT = 'impersonate' | 'remove'

type CurrentRowSimplifiedT = {
  teacherId: number
  teacherName: string
  teacherEmail: string
  schoolName: string
}

export type Props = {
  currentSchoolIds: Array<number> | undefined | null
  schools: Array<SchoolAttrs>
  tableParams: TableParamsT<TeacherTabFilteringT>
  schoolAdmin: SchoolAdminAttrs
  onUpdateTeachers: () => void
  hasRGPIdp: boolean
  summary: SummaryT
}

type State = {
  showModal: ModalToShowT | undefined | null
  currentRow: CurrentRowSimplifiedT | undefined | null
}

export function TeacherTabContainer(props: Props) {
  const {t} = useSafeTranslation()
  const [state, setState] = useState<State>({
    showModal: null,
    currentRow: null,
  })
  const navigate = useNavigate()

  const showImpersonateModal = (currentRow: CurrentRowSimplifiedT) => {
    setState({showModal: 'impersonate', currentRow})
  }

  const hideImpersonateModal = () => {
    setState({showModal: null, currentRow: null})
  }

  const impersonateTeacher = (t: TranslateT, teacherId: number) => {
    const {schoolAdmin} = props

    const classroomDashWindow = window.open('', '_blank')
    if (classroomDashWindow?.document === undefined) {
      Notification.displayErrorNotification(
        t('MANAGE_TEACHERS_TEACHER_TAB_IMPERSONATE_ERROR_NOTIFICATION'),
        IMPERSONATION_ERROR_NOTIF_WAIT,
      )
      hideImpersonateModal()
      return
    }

    classroomDashWindow.document.body.innerHTML = t(
      'MANAGE_TEACHERS_TEACHER_TAB_IMPERSONATE_AUTHENTICATING',
    )

    ApiHelper.impersonateTeacher(teacherId, schoolAdmin.id, schoolAdmin.password)
      .then(
        () => {
          hideImpersonateModal()
          classroomDashWindow.document.body.innerHTML = t(
            'MANAGE_TEACHERS_TEACHER_TAB_IMPERSONATE_LOADING',
          )
          classroomDashWindow.location.href = PATHS.teacherDashUrl
        },
        () => {
          classroomDashWindow.close()
        },
      )
      .catch(err => logErrorAsUnhandled(new HttpError(err)))
  }

  const showRemoveModal = (currentRow: CurrentRowSimplifiedT) => {
    setState({showModal: 'remove', currentRow})
  }

  const hideRemoveModal = () => {
    setState({showModal: null, currentRow: null})
  }

  const removeTeacher = (t: TranslateT, currentRow: CurrentRowSimplifiedT) => {
    const {onUpdateTeachers, tableParams} = props

    const {teacherId} = currentRow

    removeAffiliation(teacherId).then(
      () => {
        hideRemoveModal()
        fetchTableRows(tableParams)
        onUpdateTeachers()
      },
      e => {
        logErrorAsUnhandled(new HttpError(e))
        Notification.displayErrorNotification(t('MANAGE_TEACHERS_REMOVE_TEACHER_AFFILIATION_ERROR'))
        console.error('Could not remove teacher affiliation:', e)
      },
    )
  }

  const fetchTableRows = (
    newTableParams: TableParamsT<TeacherTabFilteringT>,
  ): Promise<{
    links: LinksT
    rows: Array<TableRowT>
    totalCount: number
  }> => {
    const {currentSchoolIds, schools} = props

    const allSchoolIds = schools.map(s => s.id)
    const schoolIdsToUse = fromMaybe(() => allSchoolIds, currentSchoolIds)

    return fetchTeacherTableRows(schoolIdsToUse, newTableParams).then(
      ({teacherTableRows, links, totalCount}) => {
        const rows = teacherTableRows.map(teacherTableRow => {
          const {firstName, lastName, ...rest} = teacherTableRow

          const name = [firstName, lastName].join(' ')

          const simplifiedRow = {
            teacherId: teacherTableRow.id,
            teacherName: name,
            teacherEmail: teacherTableRow.email,
            schoolName: teacherTableRow.school.name,
          }

          return {
            name,
            ...rest,
            actions: {
              showImpersonateModal: () => showImpersonateModal(simplifiedRow),
              showRemoveModal: () => showRemoveModal(simplifiedRow),
            },
          }
        })

        return {links, rows, totalCount}
      },
    )
  }

  const onUpdate = (newTableParams: TableParamsT<TeacherTabFilteringT>) => {
    const {currentSchoolIds} = props
    const {additionalFilters} = newTableParams

    const url = Routes.dashboard({
      schoolIds: currentSchoolIds,
      activeTab: 'teacher-tab',
      position: newTableParams.position,
      searchValue: newTableParams.searchValue,
      limit: newTableParams.limit,
      additionalFilters: buildTeacherTabFiltersQueryParams(newTableParams, additionalFilters),
    })
    navigate(url)
  }

  const {currentSchoolIds, schools, tableParams, summary, hasRGPIdp} = props
  const {currentRow, showModal} = state
  const {additionalFilters} = tableParams

  const impersonateModal =
    currentRow !== undefined && currentRow !== null ? (
      <ImpersonateModal
        show={showModal === 'impersonate'}
        onModalHide={hideImpersonateModal}
        name={currentRow.teacherEmail}
        email={currentRow.teacherEmail}
        impersonateTeacher={() => impersonateTeacher(t, currentRow.teacherId)}
      />
    ) : null

  const removeModal =
    currentRow !== null && currentRow !== undefined ? (
      <RemoveTeacherModal
        show={showModal === 'remove'}
        teacherName={currentRow.teacherName}
        schoolName={currentRow.schoolName}
        onModalHide={hideRemoveModal}
        removeTeacher={() => removeTeacher(t, currentRow)}
      />
    ) : null

  const filtersNode = (
    <TeacherTabFilter teacherTabFilter={additionalFilters} currentSchoolIds={currentSchoolIds} />
  )

  const schoolIdsToUse = currentSchoolIds ?? schools.map(s => s.id)

  const downloadMissingEmailsCsv = () =>
    fancyClient
      .GET('/2/school-admins/me/teachers-missing-emails', {
        headers: {
          Accept: 'text/csv',
        },
        parseAs: 'blob',
        params: {
          query: {
            schoolIds: schoolIdsToUse,
          },
        },
      })
      .then(extractDataAndHeaders)
      .then(({data, headers}) => downloadFile(data, headers, 'teachers-missing-emails.csv'))

  const missingEmailsAlert =
    summary.teachersMissingEmailCount > 0 ? (
      <div className={teachersMissingEmailAlert}>
        <Alert type="warning">
          <SafeTrans
            i18nKey="MANAGE_TEACHERS_TEACHER_TAB_MISSING_EMAIL_ALERT"
            components={{
              helpLink: (
                <Link href="https://help2.renaissance.com/freckle/204644444" reactRouter={false} />
              ),
              downloadLink: (
                <Button
                  style="link"
                  className={downloadLink}
                  leftIcon={faDownload}
                  onClick={() => downloadMissingEmailsCsv()}
                />
              ),
            }}
            values={{missingEmailCount: summary.teachersMissingEmailCount}}
          />
        </Alert>
      </div>
    ) : null

  return (
    <>
      {impersonateModal}

      {removeModal}

      {missingEmailsAlert}

      <InviteTeacherForm onSubmit={inviteTeacher(t)} />

      <TeacherTable
        currentSchoolIds={currentSchoolIds}
        tableParams={tableParams}
        fetchTableRows={fetchTableRows}
        onUpdate={onUpdate}
        filters={filtersNode}
        hasRGPIdp={hasRGPIdp}
        summary={summary}
      />
    </>
  )
}

function inviteTeacher(t: TranslateT): (name: string, email: string) => Promise<unknown> {
  return (name, email) => {
    return ApiHelper.inviteTeacher(name, email)
      .then(() => {
        Notification.displaySuccessNotification(
          t('MANAGE_TEACHERS_TEACHER_TAB_INVITE_TEACHER_SUCCESS_NOTIFICATION'),
        )
      })
      .catch(err => {
        logError(new HttpError(err))

        Notification.displayErrorNotification(
          t('MANAGE_TEACHERS_TEACHER_TAB_INVITE_TEACHER_ERROR_NOTIFICATION'),
        )
      })
  }
}

function buildTeacherTabFiltersQueryParams(
  tableParams: TableParamsT<TeacherTabFilteringT>,
  additionalFilters?: TeacherTabFilteringT | null,
): TeacherTabFilterQueryParamsT | undefined | null {
  if (additionalFilters === null || additionalFilters === undefined) {
    return null
  }
  const initialFilters = getInitialFilters(tableParams)
  return {
    tag: TEACHER_FILTER_QUERY_PARAM_TAG,
    teacherName: initialFilters.teacherName,
    email: initialFilters.email,
    schoolName: initialFilters.schoolName,
    role: initialFilters.role,
    studentGrade: initialFilters.studentGrade,
    identityProviders: initialFilters.sources,
  }
}
